import { useEffect, useRef, useCallback } from "react";
import useCurrencyStore from "../store/useCurrencyStore";
import useOrgStore from "../store/useOrgStore";
import { getCurrencySymbol } from "../services/vehicleApis";
import { findMyOrg, GetMyDetails, getTermsAndConditions } from "../services/MyProfileApi";
import { useAuth } from "../contexts/AuthContext";
import useAppConfigStore from "../store/useAppConfigStore";
import useUserStore from "../store/useUserStore";
import useTermsStore from "../store/useTermsStore";
import { FeatureListApi, getLatestReleasePopup } from "../services/CommonApi"; // ← added
import useFeatureStore from "../store/useFeatureStore";
import useReleasePopupStore from "../store/useReleasePopupStore";             // ← added
import { baseURL } from "../services/api";
import { checkFeedbackVisibility } from "../services/FeedbackApi";
import useFeedbackPopupStore from "../store/useFeedbackPopupStore";

export function useInitializeAppData() {
  const { isAuthenticated } = useAuth();
  const setCurrencySymbol = useCurrencyStore((state) => state.setCurrencySymbol);
  const setOrganization = useOrgStore((state) => state.setOrganization);
  const setAppConfig = useAppConfigStore((state) => state.setAppConfig);
  const setTerms = useTermsStore((state) => state.setTerms);
  const setUser = useUserStore((state) => state.setUser);
  const setFeatures = useFeatureStore((state) => state.setFeatures);
  const setReleasePopup = useReleasePopupStore((state) => state.setReleasePopup); // ← added
  const hasLoadedRef = useRef(false);

  const loadData = useCallback(async () => {
    try {
      const [
        currencyResponse,
        orgResponse,
        userResponse,
        termsResponse,
        featureResponse,
      ] = await Promise.all([
        getCurrencySymbol(),
        findMyOrg(),
        GetMyDetails(),
        getTermsAndConditions(),
        FeatureListApi(),
      ]);

      setCurrencySymbol(currencyResponse?.data || "$");
      setOrganization(orgResponse?.data || "");
      setAppConfig(orgResponse?.data || "");

      const userData = userResponse || "";
      if (userData?.profile_pic && !userData.profile_pic.startsWith("http")) {
        userData.profile_pic = `${baseURL.slice(0, -1)}/${userData.profile_pic.replace(/^\//, "")}`;
      }
      setUser(userData);
      setTerms(termsResponse?.data?.data);

      const featureArray = featureResponse?.data?.data || [];

      const featuresObject = {};
      featureArray.forEach((feature) => {
        featuresObject[feature.name] = feature.is_enabled;
      });
      setFeatures(featuresObject);

      const isReleasePopupEnabled = !!featuresObject["release_popup"];

      if (isReleasePopupEnabled) {
        try {
          const releaseResponse = await getLatestReleasePopup();

          const rawData = releaseResponse?.data || releaseResponse;
          const releaseData = rawData?.data || rawData;

          if (releaseData?.show_popup && releaseData?.release) {
            setReleasePopup(true, releaseData.release);
          } else {
            setReleasePopup(false, null);
          }

        } catch (error) {
          setReleasePopup(false, null);
        }
      } else {
        // 🔥 IMPORTANT: force close if disabled
        setReleasePopup(false, null);
      }

      // 🔹 Feedback popup logic
      const setFeedbackPopup =
        useFeedbackPopupStore.getState().setFeedbackPopup;

      const isFeedbackEnabled = featuresObject["feedback_popup"];

      if (isFeedbackEnabled) {
        try {
          const feedbackCheckResponse = await checkFeedbackVisibility();
          setFeedbackPopup(!!feedbackCheckResponse?.show);
        } catch (error) {
          setFeedbackPopup(false);
        }
      } else {
        setFeedbackPopup(false);
      }

      hasLoadedRef.current = true;
    } catch (error) {
      console.error("Error loading initial data:", error);
      setCurrencySymbol("$");
    }
  }, [
    setCurrencySymbol,
    setOrganization,
    setAppConfig,
    setUser,
    setTerms,
    setFeatures,
    setReleasePopup, // ← added
  ]);

  useEffect(() => {
    if (isAuthenticated && !hasLoadedRef.current) {
      loadData();
    }
  }, [isAuthenticated, loadData]);

  useEffect(() => {
    if (!isAuthenticated) {
      hasLoadedRef.current = false;
    }
  }, [isAuthenticated]);

  return { reloadAppData: loadData };
}
